/**
 * stravian-motion.js — micro-interactions the Wonder theme does not provide.
 *
 * Section entrance animation is the theme's own `scroll-trigger` system; this
 * file does NOT touch it. It adds:
 *   <span data-sl-count="4.7">   counts up when scrolled into view
 *   <details data-sl-collapse>   height-animated open/close
 *   [data-sl-carousel]           dot sync + optional autoplay for a
 *                                CSS scroll-snap track (swipe works without JS)
 *   [data-sl-wishlist="handle"]  localStorage save toggle
 *   .sl-bottomnav                proxies menu/cart clicks to the theme's own
 *                                header triggers; owns no drawer logic
 *   [data-sl-search]             in-place predictive search for the bottom bar
 *                                (the theme drawer focuses the header input,
 *                                which scrolls the page to the top)
 *
 * (data-sl-press is CSS-only, in custom.css.)
 *
 * Loaded with defer from layout/theme.liquid. All timing comes from --sl-*
 * custom properties.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ms(name, fallback) {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!v) return fallback;
    return v.slice(-2) === 'ms' ? parseFloat(v)
      : v.slice(-1) === 's' ? parseFloat(v) * 1000
      : fallback;
  }

  /* ------------------------------------------------------------- count-ups */

  function initCounters(root) {
    var els = root.querySelectorAll('[data-sl-count]:not([data-sl-counted])');
    if (!els.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-sl-counted', '');
        var n = parseFloat(el.getAttribute('data-sl-count'));
        if (!isNaN(n)) el.textContent = n.toFixed((el.getAttribute('data-sl-count').split('.')[1] || '').length);
      });
      return;
    }

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        obs.unobserve(el);
        el.setAttribute('data-sl-counted', '');

        var raw = el.getAttribute('data-sl-count');
        var target = parseFloat(raw);
        if (isNaN(target)) return;
        var decimals = (raw.split('.')[1] || '').length;

        var dur = ms('--sl-dur-slow', 420) * 2;
        var start = performance.now();
        (function tick(now) {
          var p = Math.min((now - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toFixed(decimals);
          if (p < 1) requestAnimationFrame(tick);
        })(start);
      });
    }, { threshold: 0.5 });

    els.forEach(function (el) { obs.observe(el); });
  }

  /* ------------------------------------------------- height-animated details */

  function initCollapse(root) {
    root.querySelectorAll('details[data-sl-collapse]:not([data-sl-bound])').forEach(function (d) {
      d.setAttribute('data-sl-bound', '');
      var body = d.querySelector('[data-sl-collapse-body]') || d.querySelector('summary + *');
      if (!body) return;

      d.addEventListener('click', function (e) {
        var summary = d.querySelector('summary');
        if (!summary || !summary.contains(e.target) || reduced.matches) return;

        e.preventDefault();
        var dur = ms('--sl-dur-base', 240);
        var ease = getComputedStyle(document.documentElement).getPropertyValue('--sl-ease-out').trim() || 'ease';

        if (d.open) {
          body.animate(
            [{ height: body.scrollHeight + 'px', opacity: 1 }, { height: 0, opacity: 0 }],
            { duration: dur, easing: ease }
          ).onfinish = function () { d.open = false; };
        } else {
          d.open = true;
          body.animate(
            [{ height: 0, opacity: 0 }, { height: body.scrollHeight + 'px', opacity: 1 }],
            { duration: dur, easing: ease }
          );
        }
      });
    });
  }


  /* ------------------------------------------------------------- carousels */
  /* Drives [data-sl-carousel]: keeps the dot indicators in sync with whatever
     the user scrolled to, and handles optional autoplay. The scrolling itself
     is CSS scroll-snap, so swipe and keyboard already work without this. */

  function initCarousels(root) {
    root.querySelectorAll('[data-sl-carousel]:not([data-sl-bound])').forEach(function (el) {
      el.setAttribute('data-sl-bound', '');

      var track = el.querySelector('[class$="__track"]');
      var dots = [].slice.call(el.querySelectorAll('[data-sl-goto]'));
      if (!track) return;

      var slides = [].slice.call(track.children);
      if (slides.length < 2) return;

      function currentIndex() {
        // Nearest slide to the track's scroll position, not a running counter,
        // so a half-swipe or a resize can never desync the dots.
        var best = 0, min = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs(s.offsetLeft - track.scrollLeft);
          if (d < min) { min = d; best = i; }
        });
        return best;
      }

      function sync() {
        var i = currentIndex();
        dots.forEach(function (d, di) {
          d.setAttribute('aria-selected', di === i ? 'true' : 'false');
        });
        // Only the visible slide should be reachable by tab.
        slides.forEach(function (s, si) {
          s.querySelectorAll('a, button').forEach(function (f) {
            if (si === i) { f.removeAttribute('tabindex'); }
            else { f.setAttribute('tabindex', '-1'); }
          });
        });
      }

      var raf;
      track.addEventListener('scroll', function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(sync);
      }, { passive: true });

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var i = parseInt(dot.getAttribute('data-sl-goto'), 10);
          track.scrollTo({ left: slides[i].offsetLeft, behavior: reduced.matches ? 'auto' : 'smooth' });
        });
      });

      sync();

      /* ---- autoplay ---- */
      var secs = parseFloat(el.getAttribute('data-sl-autoplay'));
      if (!secs || reduced.matches) return;

      var timer = null;

      function advance() {
        var i = (currentIndex() + 1) % slides.length;
        track.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
      }
      function play() {
        if (timer) return;
        timer = setInterval(advance, secs * 1000);
      }
      function pause() {
        clearInterval(timer);
        timer = null;
      }

      el.addEventListener('mouseenter', pause);
      el.addEventListener('mouseleave', play);
      el.addEventListener('focusin', pause);
      el.addEventListener('focusout', play);
      // A carousel rotating in a hidden tab burns battery and confuses
      // returning users, who land mid-sequence with no idea why.
      document.addEventListener('visibilitychange', function () {
        document.hidden ? pause() : play();
      });
      // Any manual interaction hands control to the visitor for good.
      track.addEventListener('pointerdown', function () { pause(); el.removeEventListener('mouseleave', play); }, { once: true });

      play();
    });
  }


  /* -------------------------------------------------------------- wishlist */
  /* Client-side only: a saved list in localStorage, no account required and no
     app dependency. It is a per-browser convenience, not a synced feature —
     if a real wishlist app is installed later, delete this and use its API. */

  var WISH_KEY = 'stravian:wishlist';

  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; }
    catch (e) { return []; }
  }

  function writeWishlist(list) {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); }
    catch (e) { /* private mode / quota — the UI still updates for this page */ }
  }

  function initWishlist(root) {
    var buttons = root.querySelectorAll('[data-sl-wishlist]:not([data-sl-bound])');
    if (!buttons.length) return;

    var saved = readWishlist();

    buttons.forEach(function (btn) {
      btn.setAttribute('data-sl-bound', '');
      var handle = btn.getAttribute('data-sl-wishlist');

      if (saved.indexOf(handle) > -1) btn.setAttribute('aria-pressed', 'true');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var list = readWishlist();
        var at = list.indexOf(handle);
        var nowOn = at === -1;

        if (nowOn) { list.push(handle); } else { list.splice(at, 1); }
        writeWishlist(list);

        btn.setAttribute('aria-pressed', nowOn ? 'true' : 'false');

        // Any other card for the same product on the page follows suit.
        document.querySelectorAll('[data-sl-wishlist="' + handle + '"]').forEach(function (other) {
          other.setAttribute('aria-pressed', nowOn ? 'true' : 'false');
        });

        document.dispatchEvent(new CustomEvent('sl:wishlist-changed', {
          detail: { handle: handle, saved: nowOn, list: list }
        }));
      });
    });
  }


  /* ------------------------------------------------------------ bottom nav */
  /* The bottom bar owns no drawer logic. Each control forwards its click to the
     theme's real header trigger, so Wonder's menu / search / cart drawers run
     untouched. If a trigger is absent (a theme setting disabled it) the control
     falls back to its href or its form, so nothing becomes a dead end. */

  function initBottomNav(root) {
    var bar = root.querySelector('.sl-bottomnav:not([data-sl-bound])');
    if (!bar) return;
    bar.setAttribute('data-sl-bound', '');

    // Marks the document so custom.css can hide the header controls the bar
    // replaces. Done from JS so that if this file fails, the header keeps its
    // own controls rather than the page losing navigation entirely.
    document.documentElement.classList.add('sl-has-bottomnav');
    document.body.style.setProperty('--sl-bottomnav-space', bar.offsetHeight + 'px');

    /* The header renders several variants of the same control (sticky vs
       static, layout option 1 vs 2) and only one is live — the others sit at
       display:none with no handler bound. Clicking the first match therefore
       does nothing, so pick the first target the browser actually renders. */
    function liveTarget(sel) {
      var all = document.querySelectorAll(sel);
      for (var i = 0; i < all.length; i++) {
        if (all[i].getClientRects().length) return all[i];
      }
      return all[0] || null;
    }

    bar.querySelectorAll('[data-sl-proxy]').forEach(function (el) {
      var sel = el.getAttribute('data-sl-proxy');

      el.addEventListener('click', function (e) {
        var target = liveTarget(sel);
        if (!target) return;              // no trigger: let href / submit happen
        e.preventDefault();
        target.click();
      });

    });

    /* ---- cart count ----
       Mirrors the theme's own bubble instead of tracking cart state here, so
       the two can never disagree. */
    var count = bar.querySelector('[data-sl-cart-count]');
    if (!count) return;

    function readThemeCount() {
      var bubble = document.querySelector('#cart-icon-bubble');
      if (!bubble) return null;
      var m = (bubble.textContent || '').match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    }

    function syncCount(n) {
      if (n === null || isNaN(n)) return;
      count.textContent = n;
      if (n > 0) { count.removeAttribute('hidden'); } else { count.setAttribute('hidden', ''); }
    }

    syncCount(readThemeCount());

    // Re-read after any of the theme's cart events.
    ['cart-drawer:refresh', 'cart:refresh', 'cart:updated'].forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(function () { syncCount(readThemeCount()); }, 120); });
    });

    // The theme replaces the bubble's markup wholesale on update, so watch it.
    var bubble = document.querySelector('#cart-icon-bubble');
    if (bubble && 'MutationObserver' in window) {
      new MutationObserver(function () { syncCount(readThemeCount()); })
        .observe(bubble, { childList: true, subtree: true, characterData: true });
    }
  }


  /* --------------------------------------------------------- inline search */
  /* The bottom bar's search field, deliberately NOT the theme's search drawer:
     that drawer finishes opening by focusing the header's input, which throws
     the viewport to the top of the page — exactly wrong for a bar pinned to the
     bottom. This queries Shopify's own /search/suggest endpoint and renders the
     results just above the bar, so the visitor never leaves the spot they are
     looking at. The <form> is a real GET to the search route, so with JS off
     (or if the fetch fails) submitting still works normally. */

  function initInlineSearch(root) {
    root.querySelectorAll('[data-sl-search]:not([data-sl-bound])').forEach(function (form) {
      form.setAttribute('data-sl-bound', '');

      var input = form.querySelector('[data-sl-search-input]');
      var panel = form.querySelector('[data-sl-search-panel]');
      if (!input || !panel) return;

      var timer = null;
      var lastQuery = '';

      function close() {
        panel.hidden = true;
        panel.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      }

      // The suggest endpoint returns a bare amount ("499.00"); the shop's own
      // money format ("Rs. {{amount}}") comes from Liquid so the currency
      // matches the rest of the storefront rather than being hard-coded.
      var moneyFormat = form.getAttribute('data-sl-money-format') || '{{amount}}';

      function money(amount) {
        if (amount === null || amount === undefined || amount === '') return '';
        return moneyFormat.replace(/\{\{\s*amount[^}]*\s*\}\}/, String(amount));
      }

      function render(products, query) {
        if (!products.length) {
          panel.innerHTML =
            '<p class="sl-search-panel__empty">No matches for &ldquo;' +
            escapeHtml(query) + '&rdquo;</p>';
          panel.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          return;
        }

        var html = products.slice(0, 6).map(function (p) {
          var img = p.featured_image && p.featured_image.url
            ? '<img class="sl-search-panel__img" src="' + p.featured_image.url +
              '&width=120" alt="" loading="lazy" width="44" height="44">'
            : '<span class="sl-search-panel__img sl-search-panel__img--none"></span>';
          return (
            '<a class="sl-search-panel__row" href="' + p.url + '" role="option">' +
              img +
              '<span class="sl-search-panel__text">' +
                '<span class="sl-search-panel__name">' + escapeHtml(p.title) + '</span>' +
                (p.price ? '<span class="sl-search-panel__price">' + money(p.price) + '</span>' : '') +
              '</span>' +
            '</a>'
          );
        }).join('');

        html += '<button class="sl-search-panel__all" type="submit">' +
                'See all results for &ldquo;' + escapeHtml(query) + '&rdquo;</button>';

        panel.innerHTML = html;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }

      function escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }

      function query(q) {
        // Shopify's own /search/suggest endpoint does fuzzy, relevance-
        // ranked matching — a query like "m" can drop products that plainly
        // contain that letter, and that ranking isn't tunable from theme
        // code. Match against the full catalog instead (same one the
        // header search uses — see assets/stravian-search.js), a plain
        // substring check, so every product containing what's typed shows.
        var catalog = (window.SLGetSearchCatalog && window.SLGetSearchCatalog()) || [];
        var needle = q.toLowerCase();
        var products = catalog.filter(function (p) {
          return p.title.toLowerCase().indexOf(needle) !== -1;
        }).map(function (p) {
          return { title: p.title, url: p.url, featured_image: p.image ? { url: p.image } : null };
        });
        render(products, q);
      }

      input.addEventListener('input', function () {
        var q = input.value.trim();
        clearTimeout(timer);

        if (q.length < 1) { close(); lastQuery = ''; return; }
        if (q === lastQuery) return;

        timer = setTimeout(function () {
          lastQuery = q;
          query(q);
        }, 120);
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); input.blur(); }
        if (e.key === 'ArrowDown') {
          var first = panel.querySelector('.sl-search-panel__row');
          if (first) { e.preventDefault(); first.focus(); }
        }
      });

      // Clicking away closes the panel, but not when the click is inside it.
      document.addEventListener('click', function (e) {
        if (!form.contains(e.target)) close();
      });

      form.addEventListener('focusout', function () {
        setTimeout(function () {
          if (!form.contains(document.activeElement)) close();
        }, 120);
      });
    });
  }

  /* ---------------------------------------------------- pack-size switcher */
  /* Stravian: products with pack-size-specific listing images (e.g. Magnesium
     60/120 tablets) render one sl-listing slider per pack, each tagged
     [data-sl-pack="60|120"]. On variant change, show the slider matching the
     selected pack and hide the other. */

  function applyPackSwitch(pack) {
    if (!pack) return;
    document.querySelectorAll('[data-sl-pack]').forEach(function (el) {
      el.hidden = el.getAttribute('data-sl-pack') !== pack;
    });
  }

  document.addEventListener('variantChangeEnd', function (e) {
    var variant = e.target && e.target.currentVariant;
    var label = variant && (variant.title || variant.option1);
    var match = label && String(label).match(/(\d+)/);
    if (match) applyPackSwitch(match[1]);
  });

  /* ------------------------------------------------------------------ boot */

  function init(root) {
    root = root || document;
    initCounters(root);
    initCollapse(root);
    initCarousels(root);
    initWishlist(root);
    initBottomNav(root);
    initInlineSearch(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('sl:content-updated', function (e) {
    init((e.detail && e.detail.root) || document);
  });

  window.StravianMotion = { init: init };
})();
